import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, DatePicker, Space, Typography, Button, message } from 'antd';
import { BarChartOutlined, DollarOutlined, CarOutlined, TeamOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Area } from '@ant-design/plots';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface Statistics {
    TotalEntries: number;
    TotalExits: number;
    UniqueCards: number;
    TotalRevenue: number;
    ActiveCards: number;
    TotalBalance: number;
}

const ParkingStatsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<Statistics | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);

    const fetchStatistics = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/v1/parking/admin/statistics', {
                params: {
                    startDate: dateRange[0].toISOString(),
                    endDate: dateRange[1].toISOString()
                }
            });

            if (response.data.success) {
                setStats(response.data.data);
            } else {
                message.error('Không thể tải thống kê');
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
            message.error('Lỗi khi tải dữ liệu thống kê');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatistics();
    }, []);

    // Mock data for chart (sẽ được thay thế bằng API thực sau)
    const statsData = [
        { date: '2024-01-16', revenue: 0, traffic: stats?.TotalEntries || 0 },
        { date: '2024-01-17', revenue: 0, traffic: stats?.TotalExits || 0 },
    ];

    const config = {
        data: statsData,
        xField: 'date',
        yField: 'traffic',
        smooth: true,
        areaStyle: {
            fill: 'l(270) 0:#ffffff 0.5:#7ec2f3 1:#1890ff',
        },
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={4}>Thống kê & Báo cáo Gửi xe</Title>

            <div style={{ marginBottom: 24 }}>
                <Space>
                    <RangePicker
                        format="DD/MM/YYYY"
                        value={dateRange}
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                setDateRange([dates[0], dates[1]]);
                            }
                        }}
                    />
                    <Button type="primary" onClick={fetchStatistics} loading={loading}>
                        Xem báo cáo
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={fetchStatistics} loading={loading}>
                        Làm mới
                    </Button>
                    <Button icon={<DownloadOutlined />}>Xuất Excel</Button>
                </Space>
            </div>

            <Row gutter={[16, 16]}>
                <Col span={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Tổng lượt vào"
                            value={stats?.TotalEntries || 0}
                            prefix={<CarOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Tổng lượt ra"
                            value={stats?.TotalExits || 0}
                            prefix={<CarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Số thẻ unique"
                            value={stats?.UniqueCards || 0}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Tổng thẻ hoạt động"
                            value={stats?.ActiveCards || 0}
                            prefix={<BarChartOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col span={16}>
                    <Card title="Biểu đồ lưu lượng xe">
                        <Area {...config} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="Thông tin tổng quan">
                        <Table
                            size="small"
                            pagination={false}
                            dataSource={[
                                { label: 'Tổng lượt vào', value: stats?.TotalEntries || 0 },
                                { label: 'Tổng lượt ra', value: stats?.TotalExits || 0 },
                                { label: 'Số thẻ unique', value: stats?.UniqueCards || 0 },
                                { label: 'Thẻ hoạt động', value: stats?.ActiveCards || 0 },
                            ]}
                            columns={[
                                { title: 'Chỉ số', dataIndex: 'label' },
                                { title: 'Giá trị', dataIndex: 'value', align: 'right' },
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ParkingStatsPage;
